import React, { useState } from 'react';
import { useDataAuth } from '../../../hooks/useDataAuth';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import VideocamIcon from '@mui/icons-material/Videocam';
import SearchIcon from '@mui/icons-material/Search';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import ImageIcon from '@mui/icons-material/Image';
import InputLabel from '@mui/material/InputLabel';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';

import { CreateNewContact } from '../../../components/whatsApp/menu/CreateNewContact';
import { updateContactResponsable, sendEmailNotification } from '../../../services/whatsApp.service';
import { ContactOptionsMenu } from './ContactOptionsMenu';
import { Dialog } from '../../others/Dialog';
import { formatHour } from '../../../utils/date';

export const MenuContacts = ({ idCompany, contacts, users, access, handleChatSelect, fetchContacts }) => {
    const [{ userSession }] = useDataAuth();
    const [searchText, setSearchText] = useState('');
    const [openDialogUsers, setOpenDialogUsers] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedContact, setSelectedContact] = useState(null);

    const renderIconForMessage = (message) => {
        const lowerMsg = message.toLowerCase();
        const styles = { fontSize: 16, color: '#8696a0', mr: 0.5 }

        if (lowerMsg.endsWith('foto')) {
            return <ImageIcon sx={styles} />;
        } else if (lowerMsg.endsWith('video')) {
            return <VideocamIcon sx={styles} />;
        }
        else if (lowerMsg.endsWith('document')) {
            return <TextSnippetIcon sx={styles} />;
        } else if (lowerMsg.endsWith('.pdf')) {
            return <PictureAsPdfIcon sx={styles} />;
        } else if (
            lowerMsg.endsWith('.doc') ||
            lowerMsg.endsWith('.docx') ||
            lowerMsg.endsWith('.csv') ||
            lowerMsg.endsWith('.xls') ||
            lowerMsg.endsWith('.xlsx') ||
            lowerMsg.endsWith('.txt') ||
            lowerMsg.endsWith('.zip')
        ) {
            return <InsertDriveFileIcon sx={styles} />;
        } else {
            return null;
        }
    };

    const handleChangeUser = async () => {
        if (selectedContact && selectedUser) {
            let data = {
                id_user: selectedUser,
                id_marketing_whatsapp: selectedContact.id_marketing_whatsapp
            };
            await updateContactResponsable(data, userSession.token);
            const responsable = users.find(u => u.id_user === selectedUser);
            const fullName = (`${responsable?.name || ''} ${responsable?.last_name || ''}`).trim();
            const name = fullName || responsable?.user_name;
            await sendEmailNotification(selectedContact, { ...responsable, name }, userSession);
            setSelectedUser(null);
            setSelectedContact(null);
            fetchContacts(access?.viewAll);
        }
    };


    const onClose = () => {
        setSelectedUser(null);
        setSelectedContact(null);
        setOpenDialogUsers(false)
    };

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 2, py: 1, pt: '60px' }}>
                <CreateNewContact idCompany={idCompany} fetchContacts={fetchContacts} />
                <Divider sx={{ my: 1, backgroundColor: '#2a3942' }} />
                <Box
                    sx={{
                        backgroundColor: '#2a3942',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        py: 0.5,
                        border: '1px solid #3b4a54',
                    }}
                >
                    <SearchIcon fontSize="small" sx={{ color: '#aebac1' }} />
                    <InputBase
                        sx={{
                            ml: 1,
                            flex: 1,
                            color: 'white',
                            fontSize: '14px',
                            '::placeholder': { color: '#aebac1', opacity: 1 }
                        }}
                        placeholder="Buscar contactos..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </Box>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    px: 0,
                    pb: 2,
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: '#3b4a54', borderRadius: '6px' },
                    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#555' },
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#3b4a54 transparent',
                }}
            >
                {contacts && contacts.length > 0 && (
                    contacts
                    .filter((contact) => contact?.name?.toLowerCase().includes(searchText.toLowerCase()))
                    .map((contact, index) => (
                        <React.Fragment key={contact.id_marketing_whatsapp}>
                            <Box
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleChatSelect(contact.id_marketing_whatsapp)
                                }}
                                sx={{
                                    p: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    "&:hover": { backgroundColor: "#2a3942" },
                                }}
                            >
                                <Badge
                                    color="success"
                                    badgeContent={contact.message_pending > 0 ? contact.message_pending : null}
                                    sx={{ mr: 2 }}
                                    overlap="circular"
                                >
                                    <Avatar src={contact.image || ""} sx={{ width: 48, height: 48 }} />
                                </Badge>
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        maxWidth: "calc(100% - 70px)",
                                        minWidth: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                    key={`${contact.id_marketing_whatsapp}-${contact.cellPhone}`}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                color: "white",
                                                mr: 1,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                            title={contact.name}
                                        >
                                            {contact.name}
                                        </Typography>

                                        {access.changeResponsability && (
                                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                                                <ContactOptionsMenu
                                                    contact={contact}
                                                    onChangeResponsable={() => {setOpenDialogUsers(true); setSelectedContact(contact)}}
                                                />
                                            </Box>

                                        )}
                                        
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            mt: 0.5,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "#8696a0",
                                                display: "flex",
                                                alignItems: "center",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {renderIconForMessage(contact.last_messages)}
                                            <span style={{ marginLeft: 4 }}>{contact.last_messages}</span>
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "#aebac1",
                                                whiteSpace: "nowrap",
                                                lineHeight: 1,
                                                mt: "2px",
                                                flexShrink: 0,
                                                fontSize: "0.75rem",
                                            }}
                                        >
                                            {formatHour(contact.date_last_message)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            {index < contacts.length - 1 && <Divider sx={{ backgroundColor: "#2a3942" }} />}
                        </React.Fragment>
                    ))
                )}
            </Box>
            {openDialogUsers && (
                <Dialog
                    openDialog={openDialogUsers}
                    widthDialog="md"
                    styleTitle={{ backgroundColor: "#233138", color: "#e9edef" }}
                    styleContent={{ backgroundColor: "#111b21", color: "#e9edef" }} 
                    styleActions={{ backgroundColor: "#233138" }} 
                    content={
                        <FormControl fullWidth>
                            <Select
                                value={selectedUser}
                                onChange={(e) => setSelectedUser(e.target.value)}
                                displayEmpty
                                sx={{
                                    bgcolor: "#233138", 
                                    color: "#e9edef",   
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#2a3942",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#3b4a54",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#00a884",
                                    }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            bgcolor: "#233138",
                                            borderRadius: "8px",
                                            minWidth: "180px",
                                            boxShadow: "0 8px 20px rgba(0,0,0,0.6)", 
                                            "& .MuiMenuItem-root": {
                                                fontSize: "0.9rem",
                                                padding: "8px 16px",
                                                color: "#e9edef",
                                                "&:hover": {
                                                    bgcolor: "#2a3942"
                                                }
                                            }
                                        }
                                    }
                                }}
                            >
                                {users.map((user) => (
                                    <MenuItem
                                        key={user.id_user}
                                        value={user.id_user}
                                    >
                                        <span style={{ color: "#fff" }}>{user.user_name}</span>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    }
                    actions={
                        <>
                            <Button onClick={onClose} sx={{ color: "#aebac1" }}>
                                Cancelar
                            </Button>
                            <Button
                                onClick={() => {
                                    if (selectedUser) {
                                        handleChangeUser();
                                        onClose();
                                    }
                                }}
                                variant="contained"
                                sx={{
                                    bgcolor: "#00a884",
                                    "&:hover": { bgcolor: "#02976f" },
                                    color: "#fff"
                                }}
                            >
                                Cambiar
                            </Button>
                        </>
                    }
                />
            )}

        </Box>
    );

}
